import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BdShareablesService } from '@bindoc-projects/bindoc-permissions-lib/data';
import { ButtonDirective } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { BdUiDialogPrimaryActionDirective } from '@bindoc-projects/ui-lib/dialog/ui';

export interface RenameFolderDialogData {
  folderId: string;
  currentName: string;
}

@Component({
  selector: 'bd-rename-folder-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3 p-4 min-w-[320px]">
      <label class="text-sm">Ordnername</label>
      <input
        pInputText
        type="text"
        required
        [(ngModel)]="folderName"
        autofocus
      />
      <div class="flex justify-end gap-2 pt-2">
        <button pButton text (click)="cancel()"><span>Abbrechen</span></button>
        <button
          pButton
          bdDialogPrimaryAction
          [disabled]="!canSubmit() || submitting()"
          (click)="submit()"
        >
          <span>Umbenennen</span>
        </button>
      </div>
    </div>
  `,
  imports: [
    BdUiDialogPrimaryActionDirective,
    FormsModule,
    ButtonDirective,
    InputTextModule,
  ],
})
export class BdRenameFolderDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config =
    inject<DynamicDialogConfig<RenameFolderDialogData>>(DynamicDialogConfig);
  private readonly _resourcesService = inject(BdShareablesService);

  public readonly folderName = signal(this._config.data?.currentName ?? '');
  public readonly submitting = signal(false);

  /**
   * Enabled only when there is a non-empty name that actually differs from the
   * current one — renaming to the same value is a no-op we suppress.
   */
  public canSubmit(): boolean {
    const name = this.folderName().trim();
    return name.length > 0 && name !== this._config.data?.currentName;
  }

  public submit(): void {
    const folderId = this._config.data?.folderId;
    if (!folderId || !this.canSubmit() || this.submitting()) {
      return;
    }
    this.submitting.set(true);
    this._resourcesService
      .renameFolder(folderId, this.folderName().trim())
      .subscribe({
        next: (folder) => this._ref.close(folder),
        error: () => {
          this.submitting.set(false);
        },
      });
  }

  public cancel(): void {
    this._ref.close(null);
  }
}
